<script>
import {ApplicationHelper} from "@/javascript/helpers/ApplicationHelper";

export default {
  name: "CampaignGalleryCard",
  props: {
    campaigns: { type: Array, default: () => [] },
    loaded: { type: Boolean, default: false },
    error: { type: Boolean, default: false }
  },
  emits: ['selectCampaign'],
  computed: {
    sortedCampaigns() {
      return [...this.campaigns]
        .filter(c => c.status === 'Active' || c.status === 'Upcoming')
        .sort((a, b) => {
          if (a.end_date && b.end_date) return new Date(a.end_date) - new Date(b.end_date);
          if (a.end_date) return -1;
          return 1;
        })
        .slice(0, 4);
    }
  },
  methods: {
    formattedMoney(val) {
      if (!val) return "$0";
      return ApplicationHelper.formatMoney(val, true);
    },
    progressPercent(campaign) {
      if (!campaign.donation_target || campaign.donation_target <= 0) return null;
      const raised = campaign.all_time_contribution_total || 0;
      return Math.min(Math.round((raised / campaign.donation_target) * 100), 100);
    },
    daysRemaining(endDate) {
      if (!endDate) return null;
      const diff = Math.ceil((new Date(endDate) - new Date()) / (1000 * 60 * 60 * 24));
      if (diff < 0) return 'Ended';
      if (diff === 0) return 'Ends today';
      if (diff === 1) return '1 day left';
      if (diff <= 7) return `${diff} days left`;
      if (diff <= 30) return `${Math.ceil(diff / 7)}w left`;
      return `${Math.ceil(diff / 30)}mo left`;
    },
    urgencyClass(endDate) {
      if (!endDate) return '';
      const diff = Math.ceil((new Date(endDate) - new Date()) / (1000 * 60 * 60 * 24));
      if (diff <= 7) return 'urgency-high';
      if (diff <= 30) return 'urgency-med';
      return '';
    },
    progressColor(pct) {
      if (pct >= 80) return '#10b981';
      if (pct >= 50) return '#f59e0b';
      return '#3b82f6';
    }
  }
};
</script>

<template>
  <div class="gallery-content">
    <div
      v-if="error"
      class="fail-state"
    >
      <i class="fa-solid fa-triangle-exclamation text-amber-400" />
      <p>Unable to load</p>
    </div>
    <template v-else-if="!loaded">
      <div
        v-for="i in 3"
        :key="i"
        class="camp-row-skel"
      >
        <div class="skel skel-name" />
        <div class="skel skel-bar" />
      </div>
    </template>
    <div
      v-else-if="sortedCampaigns.length === 0"
      class="fail-state"
    >
      <i class="fa-solid fa-flag text-gray-300" />
      <p>No active campaigns</p>
    </div>
    <template v-else>
      <div
        v-for="campaign in sortedCampaigns"
        :key="campaign.id"
        class="camp-row cursor-pointer hover:bg-gray-50 transition-colors"
        @click="$emit('selectCampaign', campaign.id)"
      >
        <div class="camp-top">
          <span class="camp-name">{{ campaign.name }}</span>
          <span
            v-if="daysRemaining(campaign.end_date)"
            :class="['camp-badge', urgencyClass(campaign.end_date)]"
          >
            {{ daysRemaining(campaign.end_date) }}
          </span>
        </div>
        <div
          v-if="progressPercent(campaign) !== null"
          class="camp-progress"
        >
          <div class="prog-bar">
            <div
              class="prog-fill"
              :style="{ width: progressPercent(campaign) + '%', backgroundColor: progressColor(progressPercent(campaign)) }"
            />
          </div>
          <span class="prog-text">{{ progressPercent(campaign) }}%</span>
        </div>
      </div>
    </template>
  </div>
</template>

<style lang="scss" scoped>
.gallery-content {
  display: flex;
  flex-direction: column;
  flex: 1;
}

.fail-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  flex: 1;
  color: #9ca3af;
  font-size: 0.8rem;
  gap: 4px;
}

.camp-row {
  padding: 8px 0;
  border-bottom: 1px solid rgba(8, 18, 37, 0.08);
  border-radius: 4px;
  &:last-child { border-bottom: none; }
}

.camp-top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 4px;
}

.camp-name { font-size: 0.8rem; font-weight: 600; color: #1f2937; }

.camp-badge {
  font-size: 0.6rem;
  padding: 1px 6px;
  border-radius: 8px;
  background: #f3f4f6;
  color: #6b7280;
}
.urgency-high { background: #fef2f2; color: #ef4444; font-weight: 600; }
.urgency-med { background: #fffbeb; color: #f59e0b; }

.camp-progress { display: flex; align-items: center; gap: 6px; }
.prog-bar { flex: 1; height: 4px; background: #f3f4f6; border-radius: 2px; overflow: hidden; }
.prog-fill { height: 100%; border-radius: 2px; transition: width 0.6s ease-out; }
.prog-text { font-size: 0.65rem; font-weight: 600; color: #374151; min-width: 28px; text-align: right; }

.camp-row-skel { padding: 10px 0; }
.skel { background: #f3f4f6; border-radius: 3px; animation: shimmer 1.5s ease-in-out infinite; }
.skel-name { width: 60%; height: 12px; margin-bottom: 6px; }
.skel-bar { width: 100%; height: 4px; }

@keyframes shimmer {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.4; }
}
</style>
